import {
  StudentHomework,
  StudentIndividualWork,
  StudentLesson,
  StudentMarkDetail,
  StudentSubjectTopic,
  StudentSubjectTopicLessons,
  StudentSubjects,
  StudentTest,
  SubjectDetails,
  SubjectStatistic,
  SubjectStatisticArrayItem,
  SubjectStatisticOther,
  TDocument,
  TEventStatus,
  TSlot,
  TTestResult,
} from '@assets/types/globals'
import { api } from '@store/api'
import {
  TScheduleByWeekOrMonth,
  TStudentEventDetail,
  TStudentEventSubject,
  TStudentProfileInfo,
  TStudentScheduleByDay,
  TStudentTeacherForEvent,
  TUrgentTask,
} from '@store/student/types'

export const studentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getStudentSubjectStatistic: builder.query<Array<SubjectStatistic>, { quarter?: number; student_id?: string }>({
      query: ({ quarter, student_id }) => ({
        url: `/students/statistics/subject/`,
        params: {
          quarter,
          student_id,
        },
      }),
    }),
    getStudentStatisticOther: builder.query<SubjectStatisticArrayItem[], { subject_id?: string; student_id?: string }>({
      query: ({ subject_id, student_id }) => ({
        url: `/students/statistics/other/`,
        params: {
          subject_id,
          student_id,
        },
      }),
      transformResponse: (response: SubjectStatisticOther) => {
        const types = Object.keys(response)
        const statisticsArray = Object.values(response)

        return statisticsArray.map((item, i) => ({
          type: types[i],
          ...item,
        }))
      },
    }),
    getStudentScheduleByDay: builder.query<TStudentScheduleByDay[], { start_date: string; end_date?: string; student_id?: string }>({
      query: ({ start_date, end_date, student_id }) => ({
        url: 'calendar_events/students/daily/',
        params: { start_date, end_date, student_id },
      }),
    }),
    getStudentScheduleByWeekOrMonth: builder.query<TScheduleByWeekOrMonth[], { start_date: string; end_date?: string }>({
      query: ({ start_date, end_date }) => ({
        url: 'calendar_events/students/',
        params: { start_date, end_date },
      }),
      providesTags: ['studentScheduleByWeekOrMonth'],
    }),
    getStudentTeachersForEvent: builder.query<TStudentTeacherForEvent[], any>({
      query: () => ({
        url: 'calendar_events/students/teachers_consult/',
      }),
    }),
    getTeacherSlotsForStudent: builder.query<TSlot[], { date: string; teacher_id: string }>({
      query: (params) => ({
        url: 'calendar_events/students/teacher_schedule/',
        params,
      }),
      providesTags: ['teacherSlotsForStudent'],
    }),
    getStudentEventSubjects: builder.query<TStudentEventSubject[], any>({
      query: (params) => ({
        url: 'calendar_events/students/subjects/',
        params,
      }),
    }),
    createStudentEvent: builder.mutation<any, { event_date: string; teacher_id: string; subject_id: string; slot: number }>({
      query: (body) => ({
        url: 'calendar_events/students/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['studentScheduleByWeekOrMonth', 'teacherSlotsForStudent'],
    }),
    updateStudentEvent: builder.mutation<any, { id: string; event_date: string; teacher_id: string; subject_id: string; slot: string; status?: TEventStatus }>({
      query: ({ id, ...body }) => ({
        url: `calendar_events/students/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['studentScheduleByWeekOrMonth', 'teacherSlotsForStudent'],
    }),
    deleteStudentEvent: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `calendar_events/students/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['studentScheduleByWeekOrMonth', 'teacherSlotsForStudent'],
    }),
    getStudentEventById: builder.query<TStudentEventDetail, { id: string }>({
      query: ({ id }) => ({
        url: `/calendar_events/students/${id}`,
      }),
    }),
    getStudentSubjects: builder.query<StudentSubjects, { student_id?: string }>({
      query: ({ student_id }) => ({
        url: `/students/subjects/`,
        params: { student_id },
      }),
    }),
    getStudentBrief: builder.query<Array<{ id: string; title: string }>, { student_id?: string }>({
      query: ({ student_id }) => ({
        url: `/students/subjects/brief/`,
        params: { student_id },
      }),
    }),
    getStudentSubjectById: builder.query<SubjectDetails, { id: string; quarter?: number }>({
      query: ({ id, quarter }) => ({
        url: `/students/subjects/${id}/`,
        params: {
          quarter,
        },
      }),
    }),
    getStudentSubjectTopicById: builder.query<StudentSubjectTopic, { topicId: string }>({
      query: ({ topicId }) => ({
        url: `/students/subjects/topic/${topicId}`,
      }),
      // transformResponse: (response: { [key: string]: any; lessons: StudentSubjectTopicLessons[] }) => response.lessons,
    }),
    getStudentLessonById: builder.query<StudentLesson, { id: string }>({
      query: ({ id }) => ({
        url: `/students/lessons/${id}`,
      }),
    }),
    getStudentHomeworkById: builder.query<StudentHomework, { id: string }>({
      query: ({ id }) => ({
        url: `/students/lessons/homework/${id}`,
      }),
      providesTags: ['studentHomework'],
    }),
    getStudentIndividualWorkById: builder.query<StudentIndividualWork, { id: string }>({
      query: ({ id }) => ({
        url: `/students/lessons/individual-work/${id}`,
      }),
      providesTags: ['studentIndividual'],
    }),
    confirmStudentIndividualWorkById: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/students/lessons/individual-work/${id}/confirm/`,
        method: 'PATCH',
      }),
      invalidatesTags: ['studentIndividual'],
    }),
    getStudentTestById: builder.query<StudentTest, { id: string }>({
      query: ({ id }) => ({
        url: `/students/lessons/test/${id}`,
      }),
      providesTags: ['studentTest'],
    }),
    sendStudentTestAnswers: builder.mutation<any, { id: string; body: TTestResult }>({
      query: ({ id, body }) => ({
        url: `/students/lessons/test/${id}/send_answers/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['studentTest'],
    }),
    confirmStudentTestById: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/students/lessons/test/${id}/confirm/`,
        method: 'PATCH',
      }),
      invalidatesTags: ['studentTest'],
    }),
    sendStudentTextHomework: builder.mutation<any, { id: string; answer: string }>({
      query: ({ id, answer }) => ({
        url: `/students/lessons/homework/${id}/send_text_homework/`,
        method: 'PATCH',
        body: { answer },
      }),
      invalidatesTags: ['studentHomework'],
    }),
    uploadStudentFileHomework: builder.mutation<TDocument, { id: string; data: FormData }>({
      query: ({ id, data }) => ({
        url: `/students/lessons/homework/${id}/file/`,
        method: 'POST',
        body: data,
      }),
    }),
    confirmStudentHomework: builder.mutation<any, { id: string; data: StudentHomework }>({
      query: ({ id, data }) => ({
        url: `/students/lessons/homework/${id}/confirm/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['studentHomework'],
    }),
    deleteStudentFileHomework: builder.mutation<any, { id: string; fileId: string }>({
      query: ({ id, fileId }) => ({
        url: `/students/lessons/homework/${id}/file/${fileId}`,
        method: 'DELETE',
      }),
    }),
    getUrgentTasks: builder.query<TUrgentTask[], { student_id?: string }>({
      query: ({ student_id }) => ({
        url: `/students/notifications/urgent_task_for_student/`,
        params: { student_id },
      }),
    }),
    getStudentMarkDetail: builder.query<StudentMarkDetail, any>({
      query: (params) => ({
        url: `/students/statistics/detail`,
        params,
      }),
    }),
    visitLessonByStudent: builder.mutation<any, string>({
      query: (id) => ({
        url: `/students/lessons/${id}/visit/`,
        method: 'PATCH',
      }),
    }),
    getStudentProfileInfo: builder.query<TStudentProfileInfo, { student_id: string }>({
      query: ({ student_id }) => ({
        url: `/detail/student/${student_id}`,
      }),
    }),
  }),
})

export const {
  useGetStudentSubjectsQuery,
  useGetStudentBriefQuery,
  useGetStudentSubjectStatisticQuery,
  useGetStudentStatisticOtherQuery,
  useGetStudentScheduleByDayQuery,
  useGetStudentScheduleByWeekOrMonthQuery,
  useGetStudentTeachersForEventQuery,
  useGetStudentEventSubjectsQuery,
  useCreateStudentEventMutation,
  useUpdateStudentEventMutation,
  useDeleteStudentEventMutation,
  useGetStudentEventByIdQuery,
  useGetTeacherSlotsForStudentQuery,
  useGetStudentSubjectByIdQuery,
  useGetStudentSubjectTopicByIdQuery,
  useGetStudentLessonByIdQuery,
  useGetStudentHomeworkByIdQuery,
  useGetStudentIndividualWorkByIdQuery,
  useConfirmStudentIndividualWorkByIdMutation,
  useGetStudentTestByIdQuery,
  useSendStudentTestAnswersMutation,
  useConfirmStudentTestByIdMutation,
  useSendStudentTextHomeworkMutation,
  useUploadStudentFileHomeworkMutation,
  useConfirmStudentHomeworkMutation,
  useDeleteStudentFileHomeworkMutation,
  useGetUrgentTasksQuery,
  useGetStudentMarkDetailQuery,
  useVisitLessonByStudentMutation,
  useGetStudentProfileInfoQuery,
} = studentApi
